import { Link } from "react-router-dom";
import { Pencil, Trash2, Flag } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { Chip, ConfirmDelete } from "./common";
import { fmtDate, fmtEUR, isPastDay, nextDeadline, progress, statutLabel } from "../lib/constants";
import { useData } from "../context/DataContext";

export const MissionCard = ({ mission, onEdit, showClient = true }) => {
  const { patch, remove, byId } = useData();
  const client = byId[mission.demande_id];
  const next = nextDeadline(mission);
  const pct = progress(mission);
  const toggle = (id, v) => patch("missions", mission.id, { livrables: mission.livrables.map((l) => (l.id === id ? { ...l, fait: !!v } : l)) });

  return (
    <article data-testid={`mission-card-${mission.id}`} className="kc-card kc-lift flex flex-col p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2"><Chip>{mission.type}</Chip><span className="text-xs text-[var(--kc-muted)]">{statutLabel(mission.statut)}</span></div>
          <h3 className="font-display text-lg font-semibold leading-snug text-[var(--kc-navy)]">{mission.titre}</h3>
          {showClient && client && <Link to={`/demandes/${client.id}`} className="text-sm text-[var(--kc-gold-dark)] hover:underline" data-testid={`mission-client-link-${mission.id}`}>{client.entreprise}</Link>}
        </div>
        <div className="flex shrink-0 gap-1">
          <button onClick={() => onEdit?.(mission)} className="rounded p-1.5 hover:bg-[var(--kc-sand)]" data-testid={`mission-edit-button-${mission.id}`}><Pencil className="h-4 w-4" /></button>
          <ConfirmDelete onConfirm={() => remove("missions", mission.id)} trigger={<button className="rounded p-1.5 hover:bg-[var(--kc-sand)]" data-testid={`mission-delete-button-${mission.id}`}><Trash2 className="h-4 w-4" /></button>} />
        </div>
      </div>

      <div className={`mt-5 flex items-center gap-2 rounded-md px-3 py-2 text-sm ${next && isPastDay(next.echeance) ? "bg-[#B42318]/[0.07] text-[#B42318]" : "bg-[var(--kc-sand)] text-[var(--kc-navy)]"}`} data-testid={`mission-next-deadline-${mission.id}`}>
        <Flag className="h-4 w-4 shrink-0" />
        {next ? <span><span className="font-semibold">Prochaine échéance · {fmtDate(next.echeance, "d MMM")}</span> — {next.titre}</span> : <span>Aucune échéance à venir</span>}
      </div>

      <div className="mt-5 space-y-2">
        {mission.livrables.map((l) => (
          <label key={l.id} className="flex cursor-pointer items-center gap-3 text-sm">
            <Checkbox checked={l.fait} onCheckedChange={(v) => toggle(l.id, v)} data-testid={`livrable-checkbox-${l.id}`} />
            <span className={`flex-1 ${l.fait ? "text-[var(--kc-muted)] line-through" : "text-[var(--kc-ink)]"}`}>{l.titre}</span>
            <span className={`font-mono text-xs ${!l.fait && isPastDay(l.echeance) ? "text-[#B42318]" : "text-[var(--kc-muted)]"}`}>{l.echeance ? fmtDate(l.echeance, "dd/MM") : ""}</span>
          </label>
        ))}
      </div>

      <div className="mt-auto pt-6">
        <div className="mb-2 flex justify-between text-xs text-[var(--kc-muted)]"><span>Livrables {pct}%</span><span className="font-mono">{fmtEUR(mission.montant)} HT</span></div>
        <Progress value={pct} className="h-1.5" data-testid={`mission-progress-${mission.id}`} />
      </div>
    </article>
  );
};
